/**
 * Clerk authentication provider wrapper.
 *
 * Provides SSO authentication for all UI users.
 * Wraps the app with ClerkProvider and provides auth hooks.
 */

import { ClerkProvider as ClerkProviderBase, useAuth, useUser } from "@clerk/clerk-react";
import { type ReactNode, useEffect, useRef } from "react";
import { setAuthTokenGetter } from "../lib/api";
import { runtimeEnv } from "../lib/runtime-config";

/**
 * Get Clerk publishable key from environment.
 * In development, auth is optional if key is not set.
 */
function getClerkPublishableKey(): string | undefined {
  // Runtime first (/config.js), build-time fallback: src/lib/runtime-config.ts
  const key = runtimeEnv("VITE_CLERK_PUBLISHABLE_KEY");
  if (key && typeof key === "string") {
    return key;
  }
  return undefined;
}

/**
 * Check if Clerk is configured.
 */
export function isClerkConfigured(): boolean {
  return Boolean(getClerkPublishableKey());
}

/**
 * Props for ClerkProvider.
 */
interface ClerkProviderProps {
  children: ReactNode;
}

/**
 * Component that initializes the API auth token getter.
 * Must be rendered inside ClerkProviderBase.
 *
 * Sets up the token getter synchronously during render (not in useEffect)
 * to avoid race conditions with queries that run on mount.
 *
 * Uses a ref to always access the latest getToken function, avoiding
 * stale closure issues when Clerk refreshes the session.
 */
function AuthTokenInitializer({ children }: { children: ReactNode }) {
  const { getToken, isSignedIn } = useAuth();
  const getTokenRef = useRef(getToken);
  const initialized = useRef(false);

  // Always keep the ref up to date with the latest getToken
  getTokenRef.current = getToken;

  // Set up token getter synchronously during render to avoid race conditions
  // with React Query hooks that run immediately on component mount.
  if (!initialized.current) {
    setAuthTokenGetter(async (options) => {
      try {
        // Protected delegated calls require the real session ID. Clerk custom
        // JWT templates (including command-session) are not session-bound.
        const tokenOptions = {
          ...(options?.forceRefresh ? { skipCache: true } : {}),
        } as const;
        return await getTokenRef.current(tokenOptions);
      } catch {
        return null;
      }
    });
    initialized.current = true;
  }

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }
    const refresh = async () => {
      try {
        await getTokenRef.current({ skipCache: true });
      } catch {
        // Best effort; request-time refresh remains as fallback.
      }
    };

    const interval = window.setInterval(() => {
      void refresh();
    }, 45 * 60_000);
    const onFocus = () => {
      void refresh();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    void refresh();

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isSignedIn]);

  return <>{children}</>;
}

/**
 * Clerk provider wrapper.
 *
 * In development mode without Clerk keys, renders children directly.
 * In production, wraps with ClerkProvider.
 */
export function ClerkProvider({ children }: ClerkProviderProps) {
  const publishableKey = getClerkPublishableKey();

  // Development mode without Clerk keys - render without auth
  if (!publishableKey) {
    console.warn(
      "[ClerkProvider] VITE_CLERK_PUBLISHABLE_KEY not set. Running without authentication.",
    );
    // Set up a mock token getter for dev mode
    setAuthTokenGetter(async () => null);
    return <>{children}</>;
  }

  return (
    <ClerkProviderBase
      publishableKey={publishableKey}
      signInUrl="/sign-in"
      signInFallbackRedirectUrl="/"
      afterSignOutUrl="/sign-in"
    >
      <AuthTokenInitializer>{children}</AuthTokenInitializer>
    </ClerkProviderBase>
  );
}

// Re-export Clerk hooks for convenience
export { useAuth, useUser };

/**
 * Default user state for when Clerk is not configured.
 */
const DEFAULT_USER = {
  isLoading: false,
  isSignedIn: true,
  user: null,
  fullName: "Dev User",
  email: "dev@localhost",
  avatarUrl: "",
};

/**
 * Hook to get current user's basic info.
 * Returns safe defaults when not authenticated or in dev mode.
 */
export function useCurrentUser() {
  // If Clerk is not configured, return dev defaults
  if (!isClerkConfigured()) {
    return DEFAULT_USER;
  }

  // This will only be called when ClerkProvider is present
  return useCurrentUserWithClerk();
}

/**
 * Internal hook that uses Clerk - only call when Clerk is configured.
 */
function useCurrentUserWithClerk() {
  const { isLoaded, isSignedIn, user } = useUser();

  if (!isLoaded) {
    return {
      isLoading: true,
      isSignedIn: false,
      user: null,
      fullName: "",
      email: "",
      avatarUrl: "",
    };
  }

  if (!isSignedIn || !user) {
    return {
      isLoading: false,
      isSignedIn: false,
      user: null,
      fullName: "",
      email: "",
      avatarUrl: "",
    };
  }

  return {
    isLoading: false,
    isSignedIn: true,
    user,
    fullName: user.fullName ?? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
    email: user.primaryEmailAddress?.emailAddress ?? "",
    avatarUrl: user.imageUrl ?? "",
  };
}

/**
 * Hook to check if user is authenticated.
 */
export function useIsAuthenticated(): boolean {
  if (!isClerkConfigured()) {
    return true; // Dev mode - always authenticated
  }
  return useIsAuthenticatedWithClerk();
}

/**
 * Internal hook that uses Clerk - only call when Clerk is configured.
 */
function useIsAuthenticatedWithClerk(): boolean {
  const { isSignedIn } = useAuth();
  return isSignedIn ?? false;
}
